// TeX math in AI documents (`$...$` inline, `$$...$$` display), rendered with KaTeX — the same
// renderer + settings the question-paper module uses (MathText.renderTex).
//
// Storage / export contract: the document HTML always holds RAW TeX (`$\frac{a}{b}$`) — that is
// what is saved and what sanitizeDocHtml returns. Rendering is DISPLAY-ONLY:
//   • editor      → renderMathInDom() swaps each formula for a non-editable KaTeX span
//                   (span.adoc-math[data-tex]); unrenderMathInDom() restores the raw TeX.
//   • print       → renderMathInHtmlString() bakes live KaTeX HTML into the print document.
//   • PDF         → rasterizeMathInHtml() turns each formula into a PNG <img> first, because
//                   html2canvas cannot draw KaTeX fractions (see the katex-fracline memory).
// Keep the math rule in sync with the backend copy (sohozkaj-backend/src/utils/mathSegments.js).
import { renderTex } from '@/pages/user/question-papers/MathText'; // also loads the KaTeX CSS
import katexCss from 'katex/dist/katex.min.css?inline';

// Pandoc-style: the opening `$` is followed by a non-space, the closing `$` preceded by a
// non-space and NOT followed by a digit — "costs $5 and $10" is not math, "$X$" and
// "$\frac{a}{b}$" are.
//   HTML  : raw markup string — inline math never spans a tag (no raw < or >).
//   TEXT  : a DOM text node's value (entities already decoded) — `$a<b$` is fine.
const MATH_HTML_SRC = /\$\$([\s\S]+?)\$\$|\$(?=\S)([^$\n<>]*?\S)\$(?!\d)/.source;
const MATH_TEXT_SRC = /\$\$([\s\S]+?)\$\$|\$(?=\S)([^$\n]*?\S)\$(?!\d)/.source;

export const hasMath = (html) => typeof html === 'string' && html.includes('$') && new RegExp(MATH_HTML_SRC).test(html);

/** Apply `fn` to every piece of `input` outside math segments; math is kept verbatim.
 *  (TeX braces like \frac{12}{y} must never be read as {field} tokens.) */
export function mapOutsideMath(input, fn, { html = true } = {}) {
  const s = String(input ?? '');
  if (!s.includes('$')) return fn(s);
  const re = new RegExp(html ? MATH_HTML_SRC : MATH_TEXT_SRC, 'g');
  let out = '';
  let last = 0;
  let m;
  while ((m = re.exec(s)) !== null) {
    out += fn(s.slice(last, m.index)) + m[0];
    last = re.lastIndex;
  }
  return out + fn(s.slice(last));
}

// One rendered formula: a non-editable span carrying the KaTeX HTML + the source TeX.
function buildMathEl(tex, display) {
  const html = renderTex(tex, display);
  if (html == null) return null;
  const span = document.createElement('span');
  span.className = 'adoc-math';
  span.setAttribute('contenteditable', 'false');
  span.setAttribute('data-tex', tex);
  if (display) span.setAttribute('data-display', '1');
  // Inline formulas stay in one piece (no line break inside a fraction); display ones centre.
  span.style.cssText = display ? 'display:block;text-align:center;margin:6px 0;' : 'display:inline-block;white-space:nowrap;';
  span.innerHTML = html;
  return span;
}

/** Replace every `$...$` / `$$...$$` in the text nodes under `root` with a rendered KaTeX span. */
export function renderMathInDom(root) {
  if (!root || typeof document === 'undefined') return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => {
      if (!n.nodeValue || !n.nodeValue.includes('$')) return NodeFilter.FILTER_REJECT;
      const p = n.parentElement;
      if (p && p.closest('.adoc-math, script, style, textarea')) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  nodes.forEach((node) => {
    const v = node.nodeValue;
    const re = new RegExp(MATH_TEXT_SRC, 'g');
    const frag = document.createDocumentFragment();
    let last = 0;
    let m;
    let any = false;
    while ((m = re.exec(v)) !== null) {
      const display = m[1] != null;
      const tex = (display ? m[1] : m[2]).trim();
      const el = tex ? buildMathEl(tex, display) : null;
      if (!el) continue; // KaTeX failed outright → leave the raw text as it is
      if (m.index > last) frag.appendChild(document.createTextNode(v.slice(last, m.index)));
      frag.appendChild(el);
      last = re.lastIndex;
      any = true;
    }
    if (!any) return;
    if (last < v.length) frag.appendChild(document.createTextNode(v.slice(last)));
    node.replaceWith(frag);
  });
}

/** Turn rendered formula spans back into their raw `$tex$` / `$$tex$$` text. */
export function unrenderMathInDom(root) {
  if (!root || typeof document === 'undefined') return;
  root.querySelectorAll('span.adoc-math').forEach((s) => {
    const tex = s.getAttribute('data-tex') || '';
    s.replaceWith(document.createTextNode(s.getAttribute('data-display') === '1' ? `$$${tex}$$` : `$${tex}$`));
  });
}

/** HTML string in → HTML string with live KaTeX spans (for print). */
export function renderMathInHtmlString(html) {
  if (typeof document === 'undefined' || !hasMath(html)) return html;
  const div = document.createElement('div');
  div.innerHTML = html;
  renderMathInDom(div);
  return div.innerHTML;
}

/** KaTeX stylesheet for a standalone (iframe) document: font urls made absolute so they load there. */
export function katexCssForIframe() {
  const origin = (typeof window !== 'undefined' && window.location && window.location.origin) || '';
  return String(katexCss).replace(/url\((['"]?)\/(?!\/)/g, `url($1${origin}/`);
}

/**
 * HTML string in → HTML string where every formula is a PNG <img> (for the PDF export).
 * Each formula is drawn by the browser (html-to-image → pixel-perfect KaTeX) and sized in `em`,
 * so it scales with the document font when the page is shrunk to fit. Falls back to the raw
 * TeX text for a formula that cannot be rasterised.
 */
export async function rasterizeMathInHtml(html, { fontPx = 14 } = {}) {
  if (typeof document === 'undefined' || !hasMath(html)) return html;
  const { toPng } = await import('html-to-image');
  const host = document.createElement('div');
  host.style.cssText = `position:fixed;left:-99999px;top:0;width:720px;font-size:${fontPx}px;line-height:1.45;color:#000;font-family:Georgia,'Times New Roman',serif;`;
  const wrap = document.createElement('div');
  wrap.innerHTML = html;
  host.appendChild(wrap);
  document.body.appendChild(host);
  try {
    renderMathInDom(wrap);
    try { await document.fonts.ready; } catch { /* best effort */ }
    for (const el of Array.from(wrap.querySelectorAll('span.adoc-math'))) {
      const tex = el.getAttribute('data-tex') || '';
      const display = el.getAttribute('data-display') === '1';
      // Measure the formula at its natural size (display math is shrunk to its content).
      el.style.cssText = 'display:inline-block;white-space:nowrap;margin:0;';
      const probe = document.createElement('span'); // zero-size inline box sitting ON the baseline
      probe.style.cssText = 'display:inline-block;width:0;height:0;vertical-align:baseline;';
      el.after(probe);
      const rect = el.getBoundingClientRect();
      const baseline = probe.getBoundingClientRect().top;
      probe.remove();
      let url = null;
      try { url = await toPng(el, { pixelRatio: 3, cacheBust: false }); } catch { url = null; }
      if (!url || !rect.width || !rect.height) {
        el.replaceWith(document.createTextNode(display ? `$$${tex}$$` : `$${tex}$`));
        continue;
      }
      const img = document.createElement('img');
      img.setAttribute('class', 'adoc-math-img'); // shares the spacing rule with the rendered spans (docHtmlCss)
      img.setAttribute('src', url);
      img.setAttribute('alt', tex);
      const descent = display ? 0 : Math.max(0, rect.bottom - baseline);
      img.setAttribute('style', `height:${(rect.height / fontPx).toFixed(3)}em;width:${(rect.width / fontPx).toFixed(3)}em;vertical-align:${(-descent / fontPx).toFixed(3)}em;`);
      if (display) {
        const box = document.createElement('div');
        box.setAttribute('style', 'text-align:center;margin:6px 0;');
        box.appendChild(img);
        el.replaceWith(box);
      } else {
        el.replaceWith(img);
      }
    }
    return wrap.innerHTML;
  } finally {
    host.remove();
  }
}
